import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../api';
import { User, AuthResponse } from '../api/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  updateUser: (userData: User) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if token is expired first
        if (authApi.clearExpiredToken()) {
          console.log('🧹 Expired token cleared during initialization');
          setUser(null);
          setLoading(false);
          return;
        }

        const storedUser = authApi.getCurrentUser();
        const isAuthenticated = authApi.isAuthenticated();
        
        console.log('🔄 Auth initialization:', {
          storedUser: !!storedUser,
          isAuthenticated,
          userEmail: storedUser?.email,
          userRole: storedUser?.role
        });

        if (storedUser && isAuthenticated) {
          // Set user from localStorage immediately
          setUser(storedUser);
          
          // Optionally verify token is still valid by getting fresh profile
          // But don't clear user data if this fails (could be network issue)
          try {
            const profileResponse = await authApi.getProfile();
            console.log('✅ Profile verification successful, updating user data');
            setUser(profileResponse);
          } catch (error: any) {
            console.warn('⚠️ Profile verification failed, but keeping stored user:', error);
            // Only clear if it's a 401 (unauthorized) error, not network errors
            if (error?.response?.status === 401) {
              console.log('🔐 Token invalid (401), clearing stored data');
              await authApi.logout();
              setUser(null);
            }
            // For other errors (network, 500, etc.), keep the stored user
          }
        } else {
          console.log('❌ No valid stored authentication found');
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Don't clear user data on initialization errors
        const storedUser = authApi.getCurrentUser();
        if (storedUser) {
          console.log('🔄 Keeping stored user despite initialization error');
          setUser(storedUser);
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<AuthResponse> => {
    setLoading(true);
    try {
      console.log('🔐 Attempting login for:', email);
      const response = await authApi.login(email, password);
      console.log('✅ Auth context received:', response);
      setUser(response.user);
      return response;
    } catch (error: any) {
      console.error('❌ Auth context login error:', error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setLoading(true);
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear user data regardless of API response
      setUser(null);
      // Clear all possible auth-related items from localStorage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpires');
      // Clear the entire localStorage to be safe
      localStorage.clear();
      setLoading(false);
      
      // Force page reload to ensure fresh start
      window.location.href = '/login';
    }
  };

  const updateUser = (userData: User) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user && authApi.isAuthenticated(),
  };

  console.log('🔐 AuthContext state:', {
    user: user?.email,
    userRole: user?.role,
    loading,
    isAuthenticated: !!user && authApi.isAuthenticated(),
    hasToken: !!localStorage.getItem('authToken'),
    hasStoredUser: !!localStorage.getItem('user')
  });

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Legacy exports for backward compatibility
export type { User } from '../api/auth';